import { useI18n, type MessageKey } from '@ecom/i18n';
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  type BadgeTone,
} from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { isoToDatetimeLocal } from '../datetime-local';
import type { CouponFormValues } from '../schemas';
import type { Coupon } from '../types';

import { formatCatalogPrice } from '@/features/products/format-price';

const STATUS_TONE: Record<'active' | 'inactive', BadgeTone> = {
  active: 'good',
  inactive: 'neutral',
};
const STATUS_LABEL_KEY: Record<'active' | 'inactive', MessageKey> = {
  active: 'merchant.products.status.active',
  inactive: 'merchant.promotions.statusInactive',
};

function formatValue(coupon: Coupon): string {
  return coupon.type === 'percentage'
    ? `${String(coupon.value)} %`
    : formatCatalogPrice(coupon.value);
}

function formatWindow(coupon: Coupon, t: ReturnType<typeof useI18n>['t']): string {
  if (coupon.validFrom === null && coupon.validUntil === null) {
    return t('merchant.promotions.noDateLimit');
  }
  const from =
    coupon.validFrom !== null ? new Date(coupon.validFrom).toLocaleDateString('fr-TN') : '…';
  const until =
    coupon.validUntil !== null ? new Date(coupon.validUntil).toLocaleDateString('fr-TN') : '…';
  return `${from} → ${until}`;
}

function formatUsage(coupon: Coupon): string {
  return coupon.usageLimitTotal === null
    ? `${String(coupon.usageCount)} / ∞`
    : `${String(coupon.usageCount)} / ${String(coupon.usageLimitTotal)}`;
}

/** Converts a real `Coupon` back into `CouponFormValues` for the edit modal. */
export function couponToFormValues(coupon: Coupon): CouponFormValues {
  return {
    code: coupon.code,
    type: coupon.type,
    value: coupon.value,
    minOrderAmount: coupon.minOrderAmount,
    usageLimitTotal: coupon.usageLimitTotal,
    usageLimitPerCustomer: coupon.usageLimitPerCustomer,
    validFrom: isoToDatetimeLocal(coupon.validFrom),
    validUntil: isoToDatetimeLocal(coupon.validUntil),
    isActive: coupon.isActive,
  };
}

export function CouponTable({
  coupons,
  onEdit,
  onToggleActive,
  onViewUsages,
  togglingId,
}: {
  coupons: readonly Coupon[];
  onEdit: (coupon: Coupon) => void;
  onToggleActive: (coupon: Coupon) => void;
  onViewUsages: (coupon: Coupon) => void;
  togglingId: string | null;
}): ReactElement {
  const { t } = useI18n();
  return (
    <Table caption={t('merchant.nav.promotions')}>
      <TableHead>
        <TableRow>
          <TableHeaderCell>{t('merchant.marketing.table.colCode')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.marketing.table.colValue')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.promotions.table.colUsage')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.promotions.table.colValidityWindow')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.orders.table.colStatus')}</TableHeaderCell>
          <TableHeaderCell> </TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {coupons.map((coupon) => (
          <TableRow key={coupon.id}>
            <TableCell>
              <span className="font-mono font-semibold text-ink">{coupon.code}</span>
            </TableCell>
            <TableCell>{formatValue(coupon)}</TableCell>
            <TableCell>{formatUsage(coupon)}</TableCell>
            <TableCell>{formatWindow(coupon, t)}</TableCell>
            <TableCell>
              <Badge tone={STATUS_TONE[coupon.isActive ? 'active' : 'inactive']}>
                {t(STATUS_LABEL_KEY[coupon.isActive ? 'active' : 'inactive'])}
              </Badge>
            </TableCell>
            <TableCell>
              <div className="flex items-center justify-end gap-3 text-xs">
                <button
                  type="button"
                  onClick={() => {
                    onEdit(coupon);
                  }}
                  className="font-medium text-accent hover:underline"
                >
                  {t('merchant.common.edit')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onViewUsages(coupon);
                  }}
                  className="font-medium text-accent hover:underline"
                >
                  {t('merchant.promotions.historyAction')}
                </button>
                <button
                  type="button"
                  disabled={togglingId === coupon.id}
                  onClick={() => {
                    onToggleActive(coupon);
                  }}
                  className="font-medium text-critical hover:underline disabled:opacity-50"
                >
                  {coupon.isActive
                    ? t('merchant.promotions.deactivateAction')
                    : t('merchant.staff.reactivateAction')}
                </button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
